"use client";

import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import MenuIcon from "../icons/MenuIcon";
import SearchIcon from "../icons/SearchIcon";
import { SheetClose } from "@/components/ui/sheet";
import CloseButton from "../icons/CloseButton";
import RightIcon from "../icons/RightIcon";
import { Input } from "@/components/ui/input";
import { useRef, useState } from "react";
import MegaMenuMobile from "./components/MegaMenuMobile";
import MegaMenuDesktop from "./components/MegaMenuDesktop";
import { NavigationMenuItem, NavigationMenuList, NavigationMenu, NavigationMenuContent, NavigationMenuTrigger, NavigationMenuLink } from "../../ui/navigation-menu";
import { businessMenuItems, manageMenuItems, resourcesMenuItems } from "./components/menu-items";
import { PopoverTrigger, Popover, PopoverContent } from "../../ui/popover";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import { GoArrowRight } from "react-icons/go";
import { FaUser } from "react-icons/fa";
import "../public-v33.css";

const initialState = [
  ...businessMenuItems,
  ...manageMenuItems,
  ...resourcesMenuItems,
].filter((item) => item.href !== "#")

type Props = {
  /**
   * Overrides the anonymous-visitor CTA ("Get Started" → /signup). Used by
   * /forms/[service] run-a-business (manage category) pages to point
   * straight at that service's filing flow instead of signup — matches the
   * reference site's navbar, where the primary CTA is "Start filing", not
   * a generic account signup (public/annual-reports.html).
   */
  ctaLabel?: string;
  ctaHref?: string;
};

export default function NavbarPublic({ ctaLabel, ctaHref }: Props = {}) {
  const cta = { label: ctaLabel ?? "Get Started", href: ctaHref ?? "/signup" };
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<string | undefined>(undefined);
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredResults = initialState.filter((item) =>
    item.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const { user } = useAuth();

  const handleStartBusinessClick = () => {
    handleBackToMainMenu();
    setSelectedMenu("start-business")
    setShowMegaMenu(true);
  };

  const handleBackToMainMenu = () => {
    setShowMegaMenu(false);
    setSelectedMenu(undefined);
  };

  const handleManageClick = () => {
    handleBackToMainMenu();
    setSelectedMenu("manage");
    setShowMegaMenu(true);
  };


  const handleProtectClick = () => {
    handleBackToMainMenu
    setSelectedMenu("protect")
    setShowMegaMenu(true);
  };

  const handleResourcesClick = () => {
    handleBackToMainMenu();
    setSelectedMenu("resources")
    setShowMegaMenu(true);
  };


  const navigationItems = [
    { name: "Manage a Business", href: "#", onMouseOver: handleManageClick },
    { name: "Compliance Platform", href: "/business-os.html" },
  ];

  return (
    <header className="w-full sticky top-0 z-50">
      <div className="mx-auto px-3.5 py-2.5">
        {/* Container */}
        <div className="relative flex items-center justify-between rounded-2xl border border-zinc-200/60 bg-white p-3 shadow-[0px_16px_5px_-13px_rgba(0,15,51,0.05)] md:rounded-[20px] md:p-4 lg:h-20">
          {/* Logo Section */}

          <Link className="flex items-center gap-3 xl:gap-4 xl:hidden" href={"/"}>
            <Image
              src={"/assets/public/landing/file-business-logo.webp"}
              alt="File Business Logo"
              width={54}
              height={36}
            />
          </Link>


          <div className="relative w-full xl:flex hidden">
            <NavigationMenu className="hidden xl:flex xl:w-full xl:min-w-full">
              <NavigationMenuList className={cn("w-full min-w-full ", user ? "gap-[calc((100vw-1000px)/2)]" : "gap-[calc((100vw-1150px)/2)]")}>
                <NavigationMenuItem>
                  <Link className="flex items-center gap-3 xl:gap-4" href={"/"}>
                    <Image
                      src={"/assets/public/landing/file-business-logo.webp"}
                      alt="File Business Logo"
                      width={128}
                      height={36}
                    />
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="v33-nav-link">
                      Start a Business
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <MegaMenuDesktop selectedMenu={'start-business'} />
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="v33-nav-link">
                      Manage a Business
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <MegaMenuDesktop selectedMenu={'manage'} />
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem className="">
                    <NavigationMenuLink href="/business-os.html" className="v33-nav-link">Compliance Platform
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem className="">
                    <NavigationMenuLink href="/tracking" className="v33-nav-link">
                      Track Order
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  {/* <NavigationMenuItem>
                    <NavigationMenuTrigger className="v33-nav-link">
                      Resources
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <MegaMenuDesktop selectedMenu={'resources'} />
                    </NavigationMenuContent>
                  </NavigationMenuItem> */}
                </NavigationMenuList>
                <NavigationMenuItem className="hidden items-center gap-5 xl:flex">
                  {/* Search Button */}
                  <Popover open={showSearch}>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full" aria-label="Search"
                        onClick={() => {
                          setShowSearch(true);
                          setTimeout(() => inputRef.current?.focus(), 100);
                        }}
                      >
                        <SearchIcon className="h-7 w-7" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-80 p-2">
                      <Input
                        ref={inputRef}
                        type="text"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        placeholder="Search..."
                        className="mb-2"
                        onBlur={() => setTimeout(() => setShowSearch(false), 200)}
                      />
                      {searchValue && (
                        <div className="max-h-60 overflow-y-auto">
                          {filteredResults.length > 0 ? (
                            filteredResults.map((item) => (
                              <Link
                                key={item.href}
                                href={item.href}
                                className="block px-3 py-2 rounded hover:bg-gray-100 text-gray-900 transition-colors"
                                onClick={() => {
                                  setShowSearch(false);
                                  setSearchValue("");
                                }}
                              >
                                {item.name}
                              </Link>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-gray-500">No results found.</div>
                          )}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>

                  {/* Auth Buttons */}
                  <div className="hidden xl:flex items-center gap-3">
                    {user ? (
                      <Button
                        variant="outline"
                        className="hidden md:flex h-11 rounded-xl border-fb-primary font-bold text-fb-primary"
                        asChild
                      >
                        <Link
                          href={"/dashboard"}
                          className="flex items-center justify-between gap-2"
                        >
                          Dashboard
                        </Link>
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          className="hidden md:flex h-11 rounded-xl border-none font-bold text-fb-primary"
                          asChild
                        >
                          <Link
                            href={"/login"}
                            className="flex items-center justify-between gap-2"
                          >
                            <FaUser className="h-5 w-5" />
                            Log in
                          </Link>
                        </Button>
                        <Button asChild className="hidden md:flex h-11 rounded-xl bg-[#101c46] hover:bg-[#0f1e4b] font-bold text-white">
                          <Link
                            href={cta.href}
                            className="flex items-center justify-between gap-2"
                          >
                            {cta.label}
                            <GoArrowRight className="h-5 w-5" />
                          </Link>
                        </Button>
                      </>
                    )}
                  </div>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Desktop Actions */}

          {/* Mobile Menu Button */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="xl:hidden"
              >
                <MenuIcon className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full max-w-full p-0 bg-white overflow-y-auto"
            >

              {showMegaMenu ? (
                <MegaMenuMobile onBack={handleBackToMainMenu} selectedMenu={selectedMenu} />
              ) : (
                <>
                  {/* Logo Section */}
                  <div className="w-full px-6 py-4 sticky top-0 border-b border-slate-900/10 flex justify-between items-center bg-white z-10">
                    <div className="flex items-center gap-3">
                      <Image
                        src={"/assets/public/landing/file-business-logo.webp"}
                        alt="File Business Logo"
                        width={54}
                        height={36}
                      />
                    </div>
                    <SheetClose asChild>
                      <button type="button" className="w-7 h-7 relative overflow-hidden">
                        <CloseButton />
                      </button>
                    </SheetClose>
                  </div>

                  <div className="min-h-[calc(100vh-73px)] px-6">
                    {/* Search Bar */}
                    <SheetTitle>
                      <div className="relative w-full">
                        <Input
                          type="text"
                          value={searchValue}
                          onChange={e => setSearchValue(e.target.value)}
                          placeholder="Search..."
                          className="rounded-[50px] px-5 py-6 text-base font-semibold placeholder:text-slate-400 outline-1 outline-offset-[-1px] outline-slate-900/10 w-full"
                        />
                        <span className="absolute right-5 top-3 pointer-events-none">
                          <SearchIcon className="w-7 h-7 text-slate-400" />
                        </span>
                        {searchValue && (
                          <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                            {filteredResults.length > 0 ? (
                              filteredResults.map(item => (
                                <Link
                                  key={item.href}
                                  href={item.href}
                                  className="block px-3 py-2 rounded hover:bg-gray-100 text-gray-900 transition-colors"
                                  onClick={() => setSearchValue("")}
                                >
                                  {item.name}
                                </Link>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-gray-500">No results found.</div>
                            )}
                          </div>
                        )}
                      </div>
                    </SheetTitle>
                    {/* Navigation Links */}
                    <div className="w-full mt-[40px] flex flex-col gap-7">
                      <div
                        className="flex justify-between items-center cursor-pointer"
                        onClick={handleStartBusinessClick}
                      >
                        <Link href="#" className="text-slate-900 text-base font-semibold leading-7">
                          Start a Business
                        </Link>
                        <RightIcon className="w-6 h-6" />
                      </div>
                      {navigationItems.map((item) => (
                        <div key={item.name} className="flex justify-between items-center" onClick={item.onMouseOver}>
                          <Link href={item.href} className="text-slate-900 text-base font-semibold leading-7">
                            {item.name}
                          </Link>
                          <RightIcon className="w-6 h-6" />
                        </div>
                      ))}
                    </div>

                    {/* Divider */}
                    <div className="w-full h-px mt-[50px] shadow-[0px_24px_8px_-20px_rgba(0,15,51,0.05)] border-b border-slate-900/10" />

                    {/* Auth Buttons */}
                    <div className="w-full mt-10 flex items-center gap-3">
                      {user ? (
                        <Button
                          variant="outline"
                          className="h-11 rounded-xl border-fb-primary font-bold text-fb-primary flex-1"
                          asChild
                        >
                          <Link
                            href={"/dashboard"}
                            className="flex items-center justify-between gap-2"
                          >
                            Dashboard
                          </Link>
                        </Button>
                      ) : (
                        <div className="w-full flex items-center gap-3">
                          <Button
                            variant="outline"
                            className="h-11 rounded-xl border-none font-bold text-fb-primary flex-1"
                            asChild
                          >
                            <Link
                              href={"/login"}
                              className="flex items-center justify-between gap-2"
                            >
                              <FaUser className="h-5 w-5" />
                              Log in
                            </Link>
                          </Button>
                          <Button asChild className="h-11 rounded-xl bg-[#101c46] hover:bg-[#0f1e4b] font-bold text-white flex-1">
                            <Link
                              href={cta.href}
                              className="flex items-center justify-between gap-2"
                            >
                              {cta.label}
                              <GoArrowRight className="h-5 w-5" />
                            </Link>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}